import {
  Box,
  Button,
  Modal as ChakraModal,
  Flex,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { JobPositionService } from '../../../services/JobPosition/JobPositionService'
import { JobPosition } from '../../../services/JobPosition/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  jobPosition?: JobPosition
}

export interface IJobPositionFields {
  name: string
  areas: string[]
}

const baseForm = {
  name: '',
  areas: ['Desenvolvimento', 'Design', 'Marketing', 'Vendas', 'Suporte']
}

const baseFormErrors = {
  name: '',
  areas: ''
}

export function JobPositionFormModal({ isOpen, onClose, jobPosition }: IProps) {
  const service = new JobPositionService()
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const [form, setForm] = useState<IJobPositionFields>(baseForm)
  const [formErrors, setFormErrors] = useState({
    name: '',
    areas: ''
  })
  const [tempArea, setTempArea] = useState('')

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)
      setForm(jobPosition ? { name: jobPosition.name, areas: jobPosition.areas || [] } : baseForm)
    }
  }, [isOpen])

  const handleSave = async () => {
    setSaving(true)

    const saveFunction = jobPosition ? service.update(jobPosition.id, form) : service.create(form)

    saveFunction
      .then(() => {
        toast({
          title: 'Vaga salva com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        onClose()
      })
      .catch((response) => {
        if (response.response.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast({
          title: 'Erro ao salvar vaga',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{jobPosition ? 'Editar' : 'Registrar'} vaga</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={3}>
            <Box>
              <FormLabel htmlFor="name">Nome</FormLabel>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                id="name"
                disabled={saving}
              />
              {formErrors.name && <Text color="red.500">{formErrors.name}</Text>}
            </Box>
            <Box>
              <FormLabel htmlFor="areas">Áreas</FormLabel>
              <Input
                value={tempArea}
                onChange={(e) => setTempArea(e.target.value)}
                placeholder="Digite uma área e pressione Enter"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tempArea.trim() !== '') {
                    e.preventDefault()

                    const areas = Array.from(new Set([...form.areas, tempArea.trim()]))
                    setForm({ ...form, areas: areas.sort((a, b) => a.localeCompare(b)) })
                    setTempArea('')
                  }
                }}
                id="areas"
                disabled={saving}
              />
              {form.areas.length > 0 && (
                <Box mt={2} style={{ maxHeight: '25 0px', overflowY: 'auto' }}>
                  {form.areas.map((area, index) => (
                    <Flex
                      key={index}
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#fff',
                        padding: '7px 15px',
                        borderRadius: '5px'
                      }}
                    >
                      <Text>{area}</Text>
                      <Button
                        size="xs"
                        colorScheme="red"
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            areas: prev.areas.filter((_, i) => i !== index)
                          }))
                        }}
                      >
                        Remover
                      </Button>
                    </Flex>
                  ))}
                </Box>
              )}
              {formErrors.areas && <Text color="red.500">{formErrors.areas}</Text>}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}
